import {
	ArrowTopRightOnSquareIcon,
	Bars3Icon,
	ChevronDownIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { Form, Link, useFetcher, useLocation } from 'react-router'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from '#app/components/ui/navigation-menu'
import { Icon } from '#app/components/ui/icon'
import { useOptimisticThemeMode } from '#app/routes/resources+/theme-switch'
import { useRequestInfo } from '#app/utils/request-info'
import { cn } from '#app/utils/misc'

/**
 * A nav link is external when its URL has a scheme. Internal links are authored
 * as paths ("/hardware-center"), external ones as full URLs, so the URL already
 * carries the answer — no extra Sanity field for an editor to set, forget, or
 * contradict.
 *
 * It also decides the element: an absolute URL must be a plain <a>, since
 * <Link> is for client-side routing within this app.
 */
function isExternalUrl(url: string | undefined): boolean {
	return /^https?:\/\//i.test(url ?? '')
}

type Theme = 'light' | 'dark'

/**
 * One link inside a nav section.
 *
 * The hover underline sits on the title alone. It used to be on the whole
 * anchor, so it ran under the description too and read as one long struck-out
 * paragraph rather than a link with a caption.
 */
const SectionNavLink = React.forwardRef<
	HTMLAnchorElement,
	{
		link: {
			title?: string
			description?: string
			url: string
			icon?: { asset?: { _ref?: string } }
		}
	} & React.ComponentPropsWithoutRef<'a'>
>(function SectionNavLink({ link, className, onClick, ...rest }, ref) {
	const external = isExternalUrl(link.url)

	/*
	 * This sits inside <NavigationMenuLink asChild>, so Radix's Slot clones the
	 * element it renders and merges its own props in — including the handler
	 * that dismisses the open menu, and a ref it needs to track the active item.
	 *
	 * A component that accepts only `link` swallows all of that, and the menu
	 * stays open after navigating. Hence forwardRef, and spreading `rest` onto
	 * the anchor. Radix's onClick must run alongside the tracking call, not
	 * instead of it.
	 */
	const track = (event: React.MouseEvent<HTMLAnchorElement>) => {
		onClick?.(event)
		trackEvent('navigation_used', {
			navigation_type: 'main_menu',
			navigation_item: link.title ?? '',
		})
	}

	const inner = (
		<>
			{link.icon?.asset?._ref && (
				<img
					src={urlFor(link.icon).width(24).height(24).url()}
					alt=""
					className="mt-0.5 h-6 w-6 flex-shrink-0"
				/>
			)}
			<div className="min-w-0 flex-1">
				<span className="text-body-md inline-flex items-center gap-1 decoration-1 underline-offset-6 group-hover:underline">
					{link.title}
					{external && (
						<ArrowTopRightOnSquareIcon
							className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0"
							aria-hidden="true"
						/>
					)}
				</span>
				{link.description && (
					<p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
						{link.description}
					</p>
				)}
			</div>
		</>
	)

	const classes = cn(
		'group flex items-start gap-3 rounded-md p-1 transition-colors hover:opacity-80',
		className,
	)

	// An absolute URL has to be a real anchor; <Link> would try to route it
	// inside the app.
	return external ? (
		<a
			{...rest}
			ref={ref}
			href={link.url}
			target="_blank"
			rel="noopener noreferrer"
			onClick={track}
			className={classes}
		>
			{inner}
			<span className="sr-only">(opens in a new tab)</span>
		</a>
	) : (
		<Link
			{...rest}
			ref={ref}
			to={link.url}
			onClick={track}
			className={classes}
		>
			{inner}
		</Link>
	)
})
import { urlFor } from '#app/utils/sanity/image'
import { type Icon as SanityIcon, type Tool } from '#app/utils/sanity/types'
import { trackEvent } from '#app/utils/tracking.ts'
import { useOptionalUser, userHasRole } from '#app/utils/user'

function MobileThemeToggle({ userPreference }: { userPreference?: Theme | null }) {
	const fetcher = useFetcher()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const iconName = mode === 'light' ? 'sun' : mode === 'dark' ? 'moon' : 'laptop'

	return (
		<fetcher.Form method="POST" action="/resources/theme-switch">
			<input type="hidden" name="redirectTo" value={requestInfo.path} />
			<input type="hidden" name="theme" value={nextMode} />
			<button
				type="submit"
				aria-label={`Switch to ${nextMode} mode`}
				className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-9 items-center justify-center rounded-md transition-colors"
			>
				<Icon name={iconName} size="md" />
			</button>
		</fetcher.Form>
	)
}

// Type definitions for our navigation structure
interface NavigationLink {
	title?: string
	description?: string
	url: string
	icon?: any
	comingSoon?: boolean
}

// Tools in the root siteSettings query are dereferenced (`tools[]->` with
// a nested `icon->`), so they arrive as full documents, not references.
type ResolvedTool = Omit<Tool, 'icon'> & {
	icon?: Pick<SanityIcon, '_id' | 'name' | 'svgCode' | 'category'> | null
}

interface NavigationSection {
	sectionType?: 'links' | 'cards'
	sectionTitle?: string
	sectionDescription?: string
	tools?: ResolvedTool[]
	links?: NavigationLink[]
}

interface NavigationItem {
	title?: string
	hasSubMenu?: boolean
	directLink?: string
	subMenuSections?: NavigationSection[]
}

// Projection of the siteSettings GROQ query in root.tsx (tools/icons
// dereferenced). Exported so root can type its fetch with the same shape.
export interface SiteSettings {
	logo?: any
	logoText?: string
	zetaLogo?: string
	footerDescription?: string
	footerMenuColumns?: Array<{
		_key?: string
		columnTitle?: string
		menuItems?: Array<{
			_key?: string
			title?: string
			url?: string
			openInNewTab?: boolean
		}>
	}>
	footerCopyright?: string
	mainNavigation?: NavigationItem[]
}

interface SiteNavigationProps {
	siteSettings?: SiteSettings
}

export function SiteNavigation({ siteSettings }: SiteNavigationProps) {
	const user = useOptionalUser()
	const isAdmin = userHasRole(user ?? null, 'admin')

	if (!siteSettings?.mainNavigation?.length) {
		return null
	}

	// Process menu sections outside of JSX
	const processMenuSections = (sections: NavigationSection[]) => {
		const allColumns: React.ReactElement[] = []

		sections.forEach((section, sectionIndex) => {
			// Handle tool card sections
			if (section.sectionType === 'cards' && section.tools?.length) {
				section.tools.forEach((tool, toolIndex) => {
					allColumns.push(
						<NavigationMenuLink
							key={`tool-${sectionIndex}-${toolIndex}`}
							asChild
						>
							<Link
								to={tool.url || '#'}
								onClick={() => trackEvent('navigation_used', { navigation_type: 'main_menu', navigation_item: tool.title ?? '' })}
								className={`bg-card hover:bg-card-hover block rounded-lg p-4 transition-all duration-200 group ${
									tool.comingSoon
										? 'pointer-events-none cursor-not-allowed opacity-50'
										: ''
								}`}
							>
								<div>
									{/* Tool Icon */}
									<div className="mb-2">
										{tool.icon?.svgCode ? (
											<div
												className="flex h-12 w-12 items-center justify-center rounded-lg -ml-2 [&>svg]:h-12 [&>svg]:w-12"
												dangerouslySetInnerHTML={{ __html: tool.icon.svgCode }}
											/>
										) : tool.customIcon?.asset?._ref ? (
											<img
												src={urlFor(tool.customIcon).width(60).height(60).url()}
												alt={tool.title ?? ''}
												className="h-12 w-12 object-contain"
											/>
										) : (
											<div className="bg-muted flex h-12 w-12 items-center justify-center rounded-lg">
												<div className="bg-primary/20 h-6 w-6 rounded" />
											</div>
										)}
									</div>

									{/* Tool Content */}
									<div>
										<h4 className="text-foreground mb-2 text-body-md font-medium group-hover:underline underline-offset-6 decoration-1 hover:opacity-80">
											{tool.title}
										</h4>
										{tool.description && (
											<p className="text-muted-foreground mb-2  line-clamp-2 text-xs">
												{tool.description}
											</p>
										)}
										{tool.comingSoon && (
											<span className="bg-muted text-muted-foreground inline-block rounded-full px-2 py-0.5 text-xs">
												Coming Soon
											</span>
										)}
									</div>
								</div>
							</Link>
						</NavigationMenuLink>,
					)
				})
				return // Skip regular link processing for card sections
			}

			// Handle regular link sections with column chunking
			const maxItemsPerColumn = 5
			const links = section.links || []
			const chunks = []

			for (let i = 0; i < links.length; i += maxItemsPerColumn) {
				chunks.push(links.slice(i, i + maxItemsPerColumn))
			}

			// If no links, create one chunk for just the header
			if (chunks.length === 0) {
				chunks.push([])
			}

			chunks.forEach((linkChunk, chunkIndex) => {
				allColumns.push(
					<div
						key={`${sectionIndex}-${chunkIndex}`}
						className={`flex flex-col ${
							chunkIndex === 0 && section.sectionTitle && sectionIndex > 0
								? 'dark:border-accent border-l border-black/20 pl-6'
								: ''
						}`}
					>
						{/* Title area - consistent height across all columns */}
						<div className="mb-3 flex min-h-[1rem] flex-col justify-end">
							{chunkIndex === 0 && section.sectionTitle && (
								<div className="pb-1">
									<h3 className="text-body-sm text-foreground leading-1 font-bold uppercase">
										{section.sectionTitle}
									</h3>
									{section.sectionDescription && (
										<p className="text-muted-foreground mt-1 text-xs">
											{section.sectionDescription}
										</p>
									)}
								</div>
							)}
						</div>
						{/* Links area - aligned across all columns */}
						{linkChunk.length > 0 && (
							<div className="space-y-1">
								{linkChunk.map((link, linkIndex) => (
									<NavigationMenuLink
										key={linkIndex}
										asChild
										className={
											link.comingSoon ? 'cursor-not-allowed opacity-50' : ''
										}
									>
										{link.comingSoon ? (
											<div className="flex items-start gap-3 rounded-md p-3">
												{link.icon?.asset?._ref && (
													<img
														src={urlFor(link.icon).width(24).height(24).url()}
														alt=""
														className="mt-0.5 h-6 w-6 flex-shrink-0"
													/>
												)}
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2">
														<span className="text-body-md">{link.title}</span>
														<span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
															Coming Soon
														</span>
													</div>
													{link.description && (
														<p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
															{link.description}
														</p>
													)}
												</div>
											</div>
										) : (
											<SectionNavLink link={link} />
										)}
									</NavigationMenuLink>
								))}
							</div>
						)}
					</div>,
				)
			})
		})

		return allColumns
	}

	return (
		<NavigationMenu className="hidden md:flex">
			<NavigationMenuList>
				{/* Static: Dashboards (admin only) */}
				{isAdmin && (
					<NavigationMenuItem>
						<NavigationMenuLink asChild>
							<Link
								to="/dashboards"
								onClick={() => trackEvent('navigation_used', { navigation_type: 'main_menu', navigation_item: 'Dashboards' })}
								className="bg-background text-body-md hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 transition-colors disabled:pointer-events-none disabled:opacity-50"
							>
								Dashboards
							</Link>
						</NavigationMenuLink>
					</NavigationMenuItem>
				)}
				{/* CMS-driven links */}
				{siteSettings.mainNavigation.map((item, index) => (
					<NavigationMenuItem key={index}>
						{item.hasSubMenu && item.subMenuSections?.length ? (
							<>
								<NavigationMenuTrigger className="text-base">
									{item.title}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<div className="w-[900px] p-8">
										<div className="grid gap-6 md:grid-cols-3">
											{processMenuSections(item.subMenuSections)}
										</div>
									</div>
								</NavigationMenuContent>
							</>
						) : (
							<NavigationMenuLink asChild>
								<Link
									to={item.directLink || '/'}
									onClick={() => trackEvent('navigation_used', { navigation_type: 'main_menu', navigation_item: item.title ?? '' })}
									className="bg-background text-body-md hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 transition-colors disabled:pointer-events-none disabled:opacity-50"
								>
									{item.title}
								</Link>
							</NavigationMenuLink>
						)}
					</NavigationMenuItem>
				))}
			</NavigationMenuList>
		</NavigationMenu>
	)
}

// Mobile Navigation Component — hamburger trigger + slide-in drawer.
export function MobileNavigation({
	siteSettings,
	themePreference,
}: SiteNavigationProps & { themePreference?: Theme | null }) {
	const [open, setOpen] = useState(false)
	const [expanded, setExpanded] = useState<Set<number>>(new Set())
	const location = useLocation()
	const user = useOptionalUser()
	const isAdmin = userHasRole(user ?? null, 'admin')

	// Close drawer on route change
	useEffect(() => {
		setOpen(false)
	}, [location.pathname])

	// Lock background scroll while open
	useEffect(() => {
		if (!open) return
		const previous = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = previous
		}
	}, [open])

	if (!siteSettings?.mainNavigation?.length) {
		return null
	}

	const toggleExpanded = (index: number) => {
		setExpanded((prev) => {
			const next = new Set(prev)
			if (next.has(index)) next.delete(index)
			else next.add(index)
			return next
		})
	}

	const trackNav = (label: string) =>
		trackEvent('navigation_used', {
			navigation_type: 'mobile_menu',
			navigation_item: label,
		})

	const renderSection = (section: NavigationSection, key: string) => {
		if (section.sectionType === 'cards' && section.tools?.length) {
			return (
				<div key={key} className="space-y-1">
					{section.sectionTitle && (
						<h4 className="text-muted-foreground px-2 pt-3 pb-1 text-xs font-bold uppercase tracking-wider">
							{section.sectionTitle}
						</h4>
					)}
					{section.tools.map((tool, i) => (
						<Link
							key={`${key}-tool-${i}`}
							to={tool.url || '#'}
							onClick={() => trackNav(tool.title ?? '')}
							className={cn(
								'flex items-center gap-3 rounded-md px-2 py-2.5 text-base hover:bg-accent',
								tool.comingSoon && 'pointer-events-none opacity-50',
							)}
						>
							{tool.icon?.svgCode ? (
								<div
									className="flex h-8 w-8 shrink-0 items-center justify-center [&>svg]:h-8 [&>svg]:w-8"
									dangerouslySetInnerHTML={{ __html: tool.icon.svgCode }}
								/>
							) : tool.customIcon?.asset?._ref ? (
								<img
									src={urlFor(tool.customIcon).width(40).height(40).url()}
									alt=""
									className="h-8 w-8 shrink-0 object-contain"
								/>
							) : null}
							<span className="flex-1">{tool.title}</span>
							{tool.comingSoon && (
								<span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
									Soon
								</span>
							)}
						</Link>
					))}
				</div>
			)
		}

		const links = section.links || []
		return (
			<div key={key} className="space-y-1">
				{section.sectionTitle && (
					<h4 className="text-muted-foreground px-2 pt-3 pb-1 text-xs font-bold uppercase tracking-wider">
						{section.sectionTitle}
					</h4>
				)}
				{links.map((link, i) =>
					link.comingSoon ? (
						<div
							key={`${key}-link-${i}`}
							className="flex items-center gap-3 rounded-md px-2 py-2.5 text-base opacity-50"
						>
							<span className="flex-1">{link.title}</span>
							<span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
								Soon
							</span>
						</div>
					) : (
						// Same rule as the desktop menu: an absolute URL is a real
						// anchor, not a client-side route.
						(() => {
							const external = isExternalUrl(link.url)
							const body = (
								<>
									{link.icon?.asset?._ref && (
										<img
											src={urlFor(link.icon).width(24).height(24).url()}
											alt=""
											className="h-5 w-5 shrink-0"
										/>
									)}
									<span className="flex-1">{link.title}</span>
									{external && (
										<ArrowTopRightOnSquareIcon
											className="text-muted-foreground h-4 w-4 shrink-0"
											aria-hidden="true"
										/>
									)}
								</>
							)
							const cls =
								'flex items-center gap-3 rounded-md px-2 py-2.5 text-base hover:bg-accent'
							return external ? (
								<a
									key={`${key}-link-${i}`}
									href={link.url}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => trackNav(link.title ?? '')}
									className={cls}
								>
									{body}
									<span className="sr-only">(opens in a new tab)</span>
								</a>
							) : (
								<Link
									key={`${key}-link-${i}`}
									to={link.url}
									onClick={() => trackNav(link.title ?? '')}
									className={cls}
								>
									{body}
								</Link>
							)
						})()
					),
				)}
			</div>
		)
	}

	return (
		<div className="md:hidden">
			<button
				type="button"
				onClick={() => setOpen(true)}
				aria-label="Open menu"
				aria-expanded={open}
				className="text-muted-foreground hover:text-foreground hover:bg-accent flex h-9 w-9 items-center justify-center rounded-md transition-colors"
			>
				<Bars3Icon className="h-6 w-6" />
			</button>

			{open && (
				<div
					className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
					onClick={() => setOpen(false)}
				>
					<div
						className="bg-background fixed inset-y-0 right-0 flex w-[88vw] max-w-sm flex-col border-l border-border shadow-xl"
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
						aria-label="Site navigation"
					>
						<div className="flex items-center justify-between border-b border-border px-4 py-3">
							<span className="text-foreground text-base font-semibold">Menu</span>
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Close menu"
								className="text-muted-foreground hover:text-foreground hover:bg-accent flex h-9 w-9 items-center justify-center rounded-md transition-colors"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						<nav className="flex-1 overflow-y-auto px-2 py-2">
							{isAdmin && (
								<Link
									to="/dashboards"
									onClick={() => trackNav('Dashboards')}
									className="flex items-center rounded-md px-3 py-3 text-base font-medium hover:bg-accent"
								>
									Dashboards
								</Link>
							)}

							<Link
								to="/bookmarks"
								onClick={() => trackNav('Bookmarks')}
								className="flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium hover:bg-accent"
							>
								<Icon name="bookmark" size="md" />
								<span>Bookmarks</span>
							</Link>

							{siteSettings.mainNavigation!.map((item, index) => {
								if (!item.hasSubMenu || !item.subMenuSections?.length) {
									return (
										<Link
											key={index}
											to={item.directLink || '/'}
											onClick={() => trackNav(item.title ?? '')}
											className="flex items-center rounded-md px-3 py-3 text-base font-medium hover:bg-accent"
										>
											{item.title}
										</Link>
									)
								}

								const isExpanded = expanded.has(index)
								return (
									<div key={index} className="border-b border-border/40 last:border-b-0">
										<button
											type="button"
											onClick={() => toggleExpanded(index)}
											aria-expanded={isExpanded}
											className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base font-medium hover:bg-accent"
										>
											<span>{item.title}</span>
											<ChevronDownIcon
												className={cn(
													'h-5 w-5 shrink-0 transition-transform',
													isExpanded && 'rotate-180',
												)}
											/>
										</button>
										{isExpanded && (
											<div className="space-y-2 pb-3 pl-3">
												{item.subMenuSections.map((section, si) =>
													renderSection(section, `${index}-${si}`),
												)}
											</div>
										)}
									</div>
								)
							})}
						</nav>

						{user && (
							<div className="border-t border-border px-2 py-3">
								<div className="flex items-center justify-between gap-2 px-3 pb-2">
									<span className="text-muted-foreground text-xs uppercase tracking-wider">
										Theme
									</span>
									<MobileThemeToggle userPreference={themePreference} />
								</div>
								<Form action="/logout" method="POST">
									<button
										type="submit"
										className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-base font-medium hover:bg-accent"
									>
										<Icon name="exit" size="md" />
										<span>Logout</span>
									</button>
								</Form>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

// Logo Component
interface LogoProps {
	siteSettings?: SiteSettings
	className?: string
}

export function SiteLogo({ siteSettings, className }: LogoProps) {
	const logoText = siteSettings?.logoText || 'The Box'

	return (
		<Link
			to="/"
			className="flex items-center gap-2 transition-opacity hover:opacity-80"
		>
			{siteSettings?.logo?.asset?._ref ? (
				<img
					src={urlFor(siteSettings.logo).height(100).url()}
					alt={logoText}
					className={className}
				/>
			) : (
				<span className="text-foreground text-xl font-bold">{logoText}</span>
			)}
		</Link>
	)
}
